import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, ThemeProvider, StyledEngineProvider } from '@mui/material';
import theme from '../theme';
import { useToast } from '../context/ToastContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Module1Service from '../services/module1Service';
import { workflowService } from '../services/api/workflowService';
import useApiMode from '../hooks/useApiMode';

// Define the DatabaseClient interface here since it's no longer exported from workflowService
interface DatabaseClient {
  client_id: number;
  name: string;
  client_code?: string;
  active?: boolean;
  keyword_count?: number;
  workflow_count?: number;
  last_activity?: string;
}

const ClientSelection: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { 
    isUsingDatabaseApproach, 
    dbClientId, 
    setDbClientId,
    convertDbClientsToIds
  } = useApiMode();
  
  const [blobClients, setBlobClients] = useState<string[]>([]);
  const [dbClients, setDbClients] = useState<DatabaseClient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newClientName, setNewClientName] = useState('');
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newClientIndustry, setNewClientIndustry] = useState('');
  
  // Load existing clients - only runs when getClients changes (it's memoized)
  useEffect(() => {
    let isMounted = true;
    
    const loadClients = async () => {
      try {
        setIsLoading(true);
        
        if (isUsingDatabaseApproach) {
          try {
            // Use workflowService.getDbClients() instead of getClients()
            const dbResult = await workflowService.getDbClients();
            console.log(`Setting ${dbResult.length} DB clients`);
            setDbClients(dbResult);
            
            // Also set string IDs for backward compatibility
            const stringIds = convertDbClientsToIds(dbResult);
            console.log(`Converted to ${stringIds.length} string IDs`);
            setBlobClients(stringIds);
            
            // If we don't have a dbClientId set yet but have clients, set the first one
            if (!dbClientId && dbResult.length > 0) {
              const firstClient = dbResult[0];
              if ('client_id' in firstClient) {
                console.log(`Setting default client ID to ${firstClient.client_id}`);
                setDbClientId(firstClient.client_id);
              }
            }
          } catch (error) {
            console.error('Error processing DB clients:', error);
            setDbClients([]);
            setBlobClients([]);
          }
        } else {
          // Use blob storage approach
          try {
            const blobResult = await Module1Service.getInstance().getClientIds();
            console.log(`Setting ${blobResult.length} blob clients (string IDs)`);
            setBlobClients(blobResult.filter(id => id !== 'default'));
          } catch (error) {
            console.error('Error fetching blob clients:', error);
            setBlobClients([]);
          }
        }
      } catch (error) {
        console.error('Error loading clients:', error);
        setDbClients([]);
        setBlobClients([]);
        showToast('Failed to load clients', 'error');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    // Call loadClients only once
    loadClients();
    
    // Cleanup function to prevent state updates after unmounting
    return () => {
      isMounted = false;
    };
  }, [isUsingDatabaseApproach, dbClientId, setDbClientId, convertDbClientsToIds, showToast]);
  
  // Handle client selection with proper type checking
  const handleSelectClient = (client: string | DatabaseClient) => {
    if (isUsingDatabaseApproach && typeof client !== 'string') {
      // Database mode - client is a DatabaseClient object
      const dbClient = client as DatabaseClient;
      if ('client_id' in dbClient) {
        // Store client ID in localStorage
        localStorage.setItem('db_client_id', dbClient.client_id.toString());
        setDbClientId(dbClient.client_id);
        
        // Use client_code if available, otherwise use client_id
        const clientIdForRoute = dbClient.client_code || dbClient.client_id.toString();
        showToast(`Client "${dbClient.name}" selected`, 'success');
        
        // Navigate to module1 with client ID in the route
        navigate(`/module1/client/${clientIdForRoute}`);
      }
    } else {
      // Blob storage mode - client is a string
      const clientId = client as string;
      
      // Store selected client ID in localStorage for global access
      localStorage.setItem('selected_client_id', clientId);
      showToast(`Client "${clientId}" selected`, 'success');
      
      // Navigate to module1 with client ID in the route
      navigate(`/module1/client/${clientId}`);
    }
  };
  
  // Handle new client creation
  const handleCreateClient = async () => {
    if (!newClientName.trim()) {
      showToast('Please enter a client name', 'error');
      return;
    }
    
    try {
      setIsCreatingClient(true);
      
      if (isUsingDatabaseApproach) {
        // Create client in database
        try {
          // Format client code - lowercase, replace spaces with dashes, remove special chars
          const clientCode = newClientName
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '');
          
          // Actually create the client in the database
          const newClient = await workflowService.createDbClient(
            newClientName,
            clientCode,
            true, // isActive
            newClientIndustry || undefined // Add industry parameter
          );
          
          // Add the new client to local state
          setDbClients(prev => [...prev, newClient]);
          
          // Store the new client ID
          localStorage.setItem('db_client_id', newClient.client_id.toString());
          setDbClientId(newClient.client_id);
          
          console.log('Created DB client:', newClient);
          
          showToast(`Client "${newClientName}" created successfully`, 'success');
          // Navigate to module1 with client ID in the route
          navigate(`/module1/client/${clientCode}`);
        } catch (error) {
          console.error('Error creating DB client:', error);
          showToast('Failed to create client in database', 'error');
        }
      } else {
        // Format client ID - lowercase, replace spaces with dashes, remove special chars
        const clientId = newClientName
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '');
        
        if (blobClients.includes(clientId)) {
          showToast('A client with this name already exists', 'error');
          return;
        }
        
        // In a real implementation, you'd call an API to create the client
        // For now, we'll just add it to the list
        setBlobClients(prev => [...prev, clientId]);
        
        // Store the new client ID
        localStorage.setItem('selected_client_id', clientId);
        
        showToast(`Client "${clientId}" created successfully`, 'success');
        // Navigate to module1 with client ID in the route
        navigate(`/module1/client/${clientId}`);
      }
    } catch (error) {
      console.error('Error creating client:', error);
      showToast('Failed to create client', 'error');
    } finally {
      setIsCreatingClient(false);
    }
  };
  
  // Filter clients based on search term
  const filteredBlobClients = blobClients.filter(clientId => 
    clientId.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Ensure dbClients is an array before filtering
  const dbClientsArray = Array.isArray(dbClients) ? dbClients : [];
  
  const filteredDbClients = dbClientsArray.filter(client => 
    client.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    (client.client_code && client.client_code.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  // Determine which list to show
  const hasClients = isUsingDatabaseApproach 
    ? dbClientsArray.length > 0 
    : blobClients.length > 0;
    
  const noClientsMatchSearch = isUsingDatabaseApproach 
    ? filteredDbClients.length === 0 
    : filteredBlobClients.length === 0;
    
  // Decide what to render based on available data
  const renderClientList = () => {
    if (isUsingDatabaseApproach && dbClientsArray.length > 0) {
      // DB mode with database clients available
      return filteredDbClients.map((client) => (
        <li key={client.client_id} className="p-3 hover:bg-gray-50">
          <div className="flex justify-between items-center">
            <div>
              <span className="font-medium">{client.name}</span>
              {client.client_code && (
                <span className="text-sm text-gray-500 ml-2">
                  ({client.client_code})
                </span>
              )}
              <div className="text-xs text-gray-500">
                ID: {client.client_id}
                {client.keyword_count !== undefined && (
                  <span className="ml-2">• {client.keyword_count.toLocaleString()} keywords</span>
                )}
                {client.workflow_count !== undefined && (
                  <span className="ml-2">• {client.workflow_count} workflows</span>
                )}
              </div>
              {client.last_activity && (
                <div className="text-xs text-gray-400">
                  Last activity: {new Date(client.last_activity).toLocaleDateString()}
                </div>
              )}
            </div>
            <Button 
              onClick={() => handleSelectClient(client)}
              size="sm"
            >
              Select
            </Button>
          </div>
        </li>
      ));
    } else {
      // Either blob storage mode or DB mode with fallback to string IDs
      return filteredBlobClients.map((clientId) => (
        <li key={clientId} className="p-3 hover:bg-gray-50">
          <div className="flex justify-between items-center">
            <span className="font-medium">{clientId}</span>
            <Button 
              onClick={() => handleSelectClient(clientId)}
              size="sm"
            >
              Select
            </Button>
          </div>
        </li>
      ));
    }
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <div className="container mx-auto py-8 space-y-8">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold">Select Client</h1>
            {isUsingDatabaseApproach && (
              <div className="text-sm bg-red-100 px-3 py-1 rounded-full text-red-800 font-medium">
                PsychoDB Mode Active
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left column - Client List */}
            <div className="lg:col-span-8">
              <Card className="p-6">
                <h2 className="text-xl font-bold mb-4">Select Existing Client</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Choose an existing client to organize your keyword analysis and prevent data from being stored under "default".
                </p>
                
                {/* Search input */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search clients..."
                    className="w-full p-2 border rounded"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                
                {/* Client list */}
                <div className="h-[400px] overflow-y-auto border rounded">
                  {isLoading ? (
                    <div className="flex justify-center items-center h-full">
                      <CircularProgress />
                    </div>
                  ) : !hasClients ? (
                    <div className="flex justify-center items-center h-full">
                      <p className="text-gray-500">
                        No clients found. Create a client to get started.
                      </p>
                    </div>
                  ) : noClientsMatchSearch ? (
                    <div className="flex justify-center items-center h-full">
                      <p className="text-gray-500">
                        No clients match your search
                      </p>
                    </div>
                  ) : (
                    <ul className="divide-y">
                      {renderClientList()}
                    </ul>
                  )}
                </div>
              </Card>
            </div>
            
            {/* Right column - Create Client */}
            <div className="lg:col-span-4">
              <Card className="p-6">
                <h2 className="text-xl font-bold mb-4">Create New Client</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Create a new client to organize your data and analysis.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Client Name</label>
                    <input 
                      type="text"
                      className="w-full p-2 border rounded"
                      value={newClientName}
                      onChange={(e) => setNewClientName(e.target.value)}
                      placeholder="Enter client name"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {isUsingDatabaseApproach ? (
                        "This will be stored in the database with a client code."
                      ) : (
                        <>
                          This will be converted to a client ID: {newClientName ? 
                            newClientName.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') 
                            : 'example-client'}
                        </>
                      )}
                    </p>
                  </div>
                  
                  {isUsingDatabaseApproach && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Industry</label>
                      <select 
                        className="w-full p-2 border rounded"
                        value={newClientIndustry}
                        onChange={(e) => setNewClientIndustry(e.target.value)}
                      >
                        <option value="">Select Industry (Optional)</option>
                        <option value="automotive">Automotive</option>
                        <option value="retail">Retail</option>
                        <option value="healthcare">Healthcare</option>
                        <option value="technology">Technology</option>
                        <option value="finance">Finance & Banking</option>
                        <option value="ecommerce">E-commerce</option>
                        <option value="travel">Travel & Hospitality</option>
                        <option value="education">Education</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  )}
                  
                  <Button 
                    onClick={handleCreateClient}
                    disabled={isCreatingClient || !newClientName.trim()}
                    className="w-full"
                  >
                    {isCreatingClient ? (
                      <>
                        <CircularProgress size={16} className="mr-2" />
                        Creating...
                      </>
                    ) : (
                      'Create Client'
                    )}
                  </Button>
                </div>
                
                <div className="mt-6">
                  <h3 className="font-semibold text-lg mb-2">Why Use Clients?</h3>
                  <ul className="text-sm space-y-2 list-disc list-inside text-gray-600">
                    <li>Organize data by client accounts</li>
                    <li>Prevent mixing data between different projects</li>
                    <li>Avoid storing everything under "default"</li>
                    <li>Better data organization on the server</li>
                  </ul>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default ClientSelection;